import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  CalendarBlank,
  Printer,
  PencilSimple,
  Check,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { StatusBadge } from "@/components/StatusBadge";
import { DensityToggle, useDensity } from "@/components/DensityToggle";
import { api } from "@/lib/api";

const FILTERS = ["All", "Requested", "Reserved", "Fabricating", "Installed"];

const daysLeft = (iso) => {
  const end = new Date(iso);
  const today = new Date();
  end.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((end - today) / 86400000);
};

const HoldCountdown = ({ id, endsAt }) => {
  if (!endsAt) return null;
  const n = daysLeft(endsAt);
  const overdue = n < 0;
  const urgent = n <= 3;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
        overdue ? "text-amber-300" : urgent ? "text-red-300" : "text-zinc-300"
      }`}
      data-testid={`hold-countdown-${id}`}
      title={
        overdue
          ? `Past its hold window since ${new Date(endsAt).toLocaleDateString()} — release it or turn it into a job`
          : `Hold drops back into stock ${new Date(endsAt).toLocaleDateString()}`
      }
    >
      <span
        className={`h-2 w-2 rounded-full ${
          overdue ? "bg-amber-400" : urgent ? "bg-red-400" : "bg-emerald-400"
        }`}
        aria-hidden="true"
      />
      {overdue
        ? `Overdue ${-n} day${n === -1 ? "" : "s"}`
        : n === 0
          ? "Hold ends today"
          : `${n} day${n === 1 ? "" : "s"} left`}
    </span>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [density, setDensity, compact] = useDensity();

  const toggle = (id) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    const [list, st] = await Promise.all([
      api.get("/reservations", { params }),
      api.get("/reservations/stats"),
    ]);
    setItems(list.data);
    setStats(st.data);
    setLoading(false);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const shown = items.filter(
    (r) => filter.length === 0 || filter.includes(r.status),
  );

  // portal requests: approve puts it on hold properly, decline frees the material
  const decide = async (r, approve) => {
    try {
      if (approve)
        await api.patch(`/reservations/${r.id}`, { status: "Reserved" });
      else await api.post(`/reservations/${r.id}/decline`);
      toast.success(
        approve
          ? `${r.tag} approved — on hold for ${r.customer_name}`
          : `${r.tag} declined — material back in stock`,
      );
      load();
    } catch {
      toast.error("Could not update that request");
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Material on Hold" />
      <main className="relative z-10 mx-auto max-w-6xl px-4 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="hold-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Inventory / Material on Hold
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Material on Hold
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                {stats
                  ? `${stats.total} reservations on record`
                  : "Loading inventory…"}
              </p>
            </div>
          </div>

          <div
            className="mt-5 flex flex-wrap gap-2 border-t border-zinc-800 pt-4"
            data-testid="hold-actions"
          >
            <Button
              data-testid="create-reservation-button"
              onClick={() => navigate("/new")}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
            >
              <Plus size={18} weight="bold" className="mr-2" /> New Reservation
            </Button>
          </div>
        </div>

        {stats && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              ["Total", stats.total, "text-white"],
              ["Requested", stats.requested, "text-amber-400"],
              ["Reserved", stats.reserved, "text-blue-400"],
              ["Fabricating", stats.fabricating, "text-yellow-400"],
              ["Installed", stats.installed, "text-emerald-400"],
            ].map(([k, v, c]) => (
              <div
                key={k}
                data-testid={`stat-${k.toLowerCase()}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              >
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  {k}
                </div>
                <div className={`font-mono-data mt-1 text-2xl font-bold ${c}`}>
                  {v}
                </div>
              </div>
            ))}
          </div>
        )}

        <FilterBar
          className="mt-8"
          testidPrefix="hold"
          search={{
            testid: "search-input",
            value: q,
            onChange: setQ,
            placeholder: "Search customer, project, color, tag…",
          }}
          sections={[
            {
              key: "status",
              label: "Status",
              multi: true,
              values: filter,
              allValue: "All",
              onChange: setFilter,
              options: FILTERS.map((f) => [f, f, `filter-${f.toLowerCase()}`]),
            },
          ]}
          right={<DensityToggle density={density} onChange={setDensity} />}
        />

        <div
          className={`mt-6 ${compact ? "space-y-1.5" : "space-y-3"}`}
          data-testid="reservation-list"
        >
          {loading &&
            [0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-24 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900"
              />
            ))}
          {!loading && shown.length === 0 && (
            <div
              data-testid="empty-state"
              className="rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            >
              <p className="text-sm text-zinc-400">
                No reservations match this view.
              </p>
            </div>
          )}
          {!loading &&
            shown.map((r) => (
              <div
                key={r.id}
                className={`flex gap-3 rounded-lg border border-zinc-800 bg-zinc-900 transition-colors duration-150 hover:border-zinc-700 ${
                  compact ? "items-center px-3 py-2" : "items-start p-4"
                }`}
              >
                <Checkbox
                  data-testid={`select-checkbox-${r.id}`}
                  checked={selected.includes(r.id)}
                  onCheckedChange={() => toggle(r.id)}
                  className="h-5 w-5 border-zinc-600 data-[state=checked]:border-indigo-500 data-[state=checked]:bg-indigo-500 data-[state=checked]:text-white"
                />
                <Link
                  to={`/reservation/${r.id}`}
                  data-testid={`reservation-card-${r.id}`}
                  className="min-w-0 flex-1"
                >
                  <div
                    className={`flex gap-3 ${
                      compact ? "items-center" : "items-start justify-between"
                    }`}
                  >
                    <div
                      className={
                        compact ? "flex min-w-0 items-center gap-3" : "min-w-0"
                      }
                    >
                      <div className="font-mono-data text-xs text-yellow-500">
                        {r.tag}
                      </div>
                      <h3
                        className={
                          compact
                            ? "truncate text-sm font-bold"
                            : "truncate text-lg font-bold"
                        }
                      >
                        {r.customer_name}
                      </h3>
                      <p
                        className={
                          compact
                            ? "truncate text-xs text-zinc-400"
                            : "truncate text-sm text-zinc-400"
                        }
                      >
                        {r.project_name}
                      </p>
                    </div>
                    {compact ? (
                      <div className="ml-auto flex shrink-0 items-center gap-3">
                        <StatusBadge
                          status={r.status}
                          testId={`status-badge-${r.id}`}
                        />
                      </div>
                    ) : (
                      <StatusBadge
                        status={r.status}
                        testId={`status-badge-${r.id}`}
                      />
                    )}
                  </div>
                  <div
                    className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400 ${
                      compact ? "mt-1" : "mt-3 gap-x-5"
                    }`}
                  >
                    <span>
                      <span className="text-zinc-600">Color </span>
                      {r.slab_color}
                    </span>
                    <span>
                      <span className="text-zinc-600">Sink </span>
                      {r.sink || "—"}
                      {r.sink_location ? ` · ${r.sink_location}` : ""}
                    </span>
                    <span>
                      <span className="text-zinc-600">Qty </span>
                      <span className="font-mono-data">{r.quantity}</span>
                    </span>
                    {r.install_date && (
                      <span className="font-mono-data inline-flex items-center gap-1">
                        <CalendarBlank size={13} /> {r.install_date}
                      </span>
                    )}
                    <HoldCountdown id={r.id} endsAt={r.hold_ends_at} />
                    {r.source === "Website hold" && (
                      <span
                        data-testid={`website-hold-badge-${r.id}`}
                        className="inline-flex items-center rounded-full border border-yellow-500/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-yellow-500"
                      >
                        Website hold · 48h
                      </span>
                    )}
                  </div>
                </Link>
                <button
                  data-testid={`reservation-edit-${r.id}`}
                  onClick={() => navigate(`/reservation/${r.id}/edit`)}
                  title="Edit reservation"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300"
                >
                  <PencilSimple size={18} weight="bold" />
                </button>
                {r.status === "Requested" && (
                  <div className="flex shrink-0 flex-col gap-1.5">
                    <button
                      data-testid={`reservation-approve-${r.id}`}
                      onClick={() => decide(r, true)}
                      title="Approve this portal request"
                      className="flex h-10 items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-emerald-300 transition-colors duration-150 hover:bg-emerald-500/20"
                    >
                      <Check size={14} weight="bold" /> Approve
                    </button>
                    <button
                      data-testid={`reservation-decline-${r.id}`}
                      onClick={() => decide(r, false)}
                      title="Turn it down and put the material back in stock"
                      className="flex h-10 items-center gap-1.5 rounded-md border border-zinc-800 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
                    >
                      <X size={14} weight="bold" /> Decline
                    </button>
                  </div>
                )}
              </div>
            ))}
        </div>
      </main>

      <div className="no-print fixed bottom-0 left-0 right-0 z-20 border-t border-zinc-800 bg-zinc-950 p-3 sm:hidden">
        <Button
          data-testid="create-reservation-button-mobile"
          onClick={() => navigate("/new")}
          className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
        >
          <Plus size={18} weight="bold" className="mr-2" /> New Reservation
        </Button>
      </div>

      {selected.length > 0 && (
        <div
          data-testid="batch-bar"
          className="no-print fixed bottom-16 left-0 right-0 z-30 border-t border-yellow-500/40 bg-zinc-900 p-3 sm:bottom-0"
        >
          <div className="mx-auto flex max-w-6xl items-center gap-3 px-1">
            <span
              className="text-sm text-zinc-300"
              data-testid="batch-selected-count"
            >
              {selected.length} selected
            </span>
            <button
              data-testid="clear-selection-button"
              onClick={() => setSelected([])}
              className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
            >
              Clear
            </button>
            <Button
              data-testid="print-selected-button"
              onClick={() =>
                navigate(`/batch-print?type=jobs&ids=${selected.join(",")}`)
              }
              className="ml-auto h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Printer size={18} weight="bold" className="mr-2" /> Print{" "}
              {selected.length} Labels
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
